'use client'

import * as React from 'react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { cn } from 'app/lib/utils'
import { Check } from 'iconoir-react'
import { ComboInputSearch } from './combo-input-search'
import { UnreadBadge } from 'app/components/inbox/UnreadBadge'
import NameIcon from 'app/components/shared/NameIcon'
import { CustomOption } from 'app/components/shared/styled/CustomOption'
import Dots from 'app/components/shared/styled/Dots'
import { Tooltip } from 'app/components/Tooltip'
import { OptionType } from 'types/OptionType'

const DEFAULT_DESELECT_ALL_OPTIONS = [{ name: `All`, id: `0`, value: `` }]

type ComboOptionsViewProps = {
  open: boolean
  forceOpen?: boolean
  value?: OptionType
  optionsContainerClassName?: string
  showSearchInDropdown?: boolean
  onSearchValueChange?: (text: string) => void
  onClearSearch?: () => void
  loading: boolean
  options?: OptionType[]
  optionClassName?: string
  enableDeselectAllOption?: boolean
  deselectAllOptions?: OptionType[]
  multiple?: boolean
  selected?: OptionType[]
  onSelect: (option: OptionType) => void
  onAddNewValue?: (val: string) => void
  checkboxSide?: `left` | `right`
  allowDeselectOption?: boolean
  addText?: string
  isOptionsGrouped?: boolean
}

// Bucket options by their `meta.group`, preserving first-seen group order (options arrive
// pre-sorted). Used to render collapsible group headers in grouped multi-select dropdowns.
function groupOptionsByMeta(options: OptionType[]): [string, OptionType[]][] {
  const groups: Record<string, OptionType[]> = {}
  const order: string[] = []
  for (const option of options) {
    const group = option.meta?.group ?? `Other`
    if (!groups[group]) {
      groups[group] = []
      order.push(group)
    }
    groups[group].push(option)
  }
  return order.map((group) => [group, groups[group]])
}

function OptionRow({
  option,
  value,
  optionClassName,
  multiple,
  selected,
  enableDeselectAllOption,
  deselectAllOptions,
  checkboxSide = `right`,
  allowDeselectOption,
  truncateLabel,
  onSelect,
}: {
  option: OptionType
  value?: OptionType
  optionClassName?: string
  multiple?: boolean
  selected?: OptionType[]
  enableDeselectAllOption?: boolean
  deselectAllOptions?: OptionType[]
  checkboxSide?: `left` | `right`
  allowDeselectOption?: boolean
  truncateLabel?: boolean
  onSelect: (option: OptionType) => void
}) {
  const isSelected = multiple ? (selected?.some((s) => s.id === option.id) ?? false) : value?.name === option.name
  const isDisabled = option.meta?.isUsed || option.meta?.isDisabled

  // Set on hover, since whether a label clipped can only be measured once it is laid out.
  const [clippedName, setClippedName] = React.useState<string | undefined>()
  const tooltipMessage =
    [clippedName, option.meta?.isUsed ? option.meta?.tooltipForDisabledOption : undefined]
      .filter(Boolean)
      .join(` · `) || undefined

  // The checkmark sits after the label when it renders on the right, so hiding it costs nothing.
  // On the left it leads the row, so it must keep its gutter when unselected or the labels of
  // selected and unselected options would not line up.
  const checkClassName =
    checkboxSide === `right`
      ? cn(`ml-4 shrink-0`, isSelected ? `block` : `hidden`)
      : cn(`mr-4 shrink-0`, !isSelected && `invisible`)

  return (
    <div
      className={
        enableDeselectAllOption && deselectAllOptions?.some((d) => d.value === option.value) ? `filter-option-all` : ``
      }
    >
      <Tooltip placement="top" message={tooltipMessage}>
        <div
          data-option-id={option.id}
          data-testid={`combo-option-${option.id}`}
          onClick={() => !isDisabled && onSelect(option)}
          className={cn(
            `combobox-option`,
            optionClassName,
            isDisabled && `!text-subtle`,
            !isDisabled && `hover:bg-(--menu-hover) cursor-pointer`,
          )}
        >
          <div
            className={cn(
              `flex w-full items-center`,
              checkboxSide === `right` ? `justify-between` : `flex-row-reverse justify-end`,
            )}
          >
            {option.meta?.customComboOption == null ? (
              <span className={cn(`flex items-center whitespace-pre-wrap`, truncateLabel && `min-w-0 flex-1`)}>
                {option.meta?.integrationStatus && (
                  <Tooltip
                    placement="left"
                    message={
                      option.meta?.integrationStatus === `integrated`
                        ? `Connected and syncing`
                        : option.meta?.integrationStatus === `missingData`
                          ? option.meta?.statusLabel || `Sync stalled`
                          : `Not connected to a PMS`
                    }
                  >
                    <span
                      className={cn(
                        `mr-4 inline-block h-2 w-2 rounded-full`,
                        { [`bg-green-600`]: option.meta.integrationStatus === `integrated` },
                        { [`bg-red-600`]: option.meta.integrationStatus === `notIntegrated` },
                        { [`bg-yellow-600`]: option.meta.integrationStatus === `missingData` },
                      )}
                    />
                  </Tooltip>
                )}
                <span
                  className={cn(
                    `flex items-center gap-x-2 font-medium`,
                    checkboxSide === `right` ? `flex-row-reverse` : ``,
                    truncateLabel && `min-w-0`,
                  )}
                >
                  <span className={cn(`flex flex-col`, truncateLabel && `min-w-0`)}>
                    <span
                      className={cn(truncateLabel && `truncate`)}
                      onMouseOver={(event) =>
                        truncateLabel &&
                        setClippedName(
                          event.currentTarget.scrollWidth > event.currentTarget.clientWidth ? option.name : undefined,
                        )
                      }
                    >
                      {option.name}
                      {/* Colour alone cannot say what is wrong. The label reuses the exact
                          wording of the coverage notice so both surfaces read as one system. */}
                      {option.meta?.statusLabel && (
                        <span className="ml-2 font-normal normal-case text-subtle">{option.meta.statusLabel}</span>
                      )}
                    </span>
                    {option.meta?.subtext && <span className="text-body-xs text-subtle">{option.meta.subtext}</span>}
                  </span>
                  {option.meta?.icon && (
                    <img
                      alt={option.meta?.iconAlt ?? `option icon`}
                      className="inline h-auto w-4"
                      src={option.meta?.icon}
                    />
                  )}
                  {option.meta?.nameInitial && (
                    <NameIcon
                      fullName={option.name[0]}
                      heightClassName={option.meta?.subtext ? `h-8` : `h-5`}
                      widthClassName={option.meta?.subtext ? `w-8` : `w-5`}
                      textClassName={option.meta?.textClassName}
                      backgroundClassName={option.meta?.backgroundClassName}
                      borderClassName={option.meta?.borderClassName}
                      isTiny
                    />
                  )}
                </span>
              </span>
            ) : (
              !multiple && <CustomOption selected={isSelected}>{option.meta.customComboOption}</CustomOption>
            )}
            {multiple ? (
              option.meta?.customComboOption != null ? (
                <CustomOption selected={isSelected}>{option.meta.customComboOption}</CustomOption>
              ) : (
                <div className={cn(`flex h-5 items-center`, checkboxSide === `right` ? `ml-4` : `mr-4`)}>
                  <input
                    name="option-checkbox"
                    type="checkbox"
                    readOnly
                    checked={isSelected}
                    className="h-4 w-4 rounded border-(--control-border) text-default outline-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                  />
                </div>
              )
            ) : allowDeselectOption ? (
              <Check className={checkClassName} />
            ) : (
              <div className="flex items-center gap-x-1">
                <Check className={checkClassName} />
                {option.meta?.unread > 0 && (
                  <div className="text-right">
                    <UnreadBadge value={option.meta?.unread} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Tooltip>
      {/* Render children (nested options) */}
      {!!option.children &&
        option.children.map((childOption) => (
          <OptionRow
            key={`${childOption.id}-${childOption.name}`}
            option={childOption}
            value={value}
            optionClassName={cn(optionClassName, `mx-6`)}
            multiple={multiple}
            selected={selected}
            enableDeselectAllOption={enableDeselectAllOption}
            deselectAllOptions={deselectAllOptions}
            checkboxSide={checkboxSide}
            allowDeselectOption={allowDeselectOption}
            truncateLabel={truncateLabel}
            onSelect={onSelect}
          />
        ))}
      {/* Render grandchildren */}
      {!!option.children &&
        option.children.map(
          (childOption) =>
            !!childOption.children &&
            childOption.children.map((grandChildOption) => (
              <OptionRow
                key={`${grandChildOption.id}-${grandChildOption.name}`}
                option={grandChildOption}
                value={value}
                optionClassName={cn(optionClassName, `mx-12 !py-1.5 !italic`)}
                multiple={multiple}
                selected={selected}
                enableDeselectAllOption={enableDeselectAllOption}
                deselectAllOptions={deselectAllOptions}
                checkboxSide={checkboxSide}
                allowDeselectOption={allowDeselectOption}
                truncateLabel={truncateLabel}
                onSelect={onSelect}
              />
            )),
        )}
    </div>
  )
}

export function ComboOptionsView({
  open,
  forceOpen,
  value,
  optionsContainerClassName,
  showSearchInDropdown = true,
  onSearchValueChange,
  onClearSearch,
  loading,
  options,
  optionClassName,
  enableDeselectAllOption,
  deselectAllOptions = DEFAULT_DESELECT_ALL_OPTIONS,
  multiple = false,
  selected,
  onSelect,
  onAddNewValue,
  checkboxSide = `right`,
  allowDeselectOption = false,
  addText = `Add new value`,
  isOptionsGrouped = false,
}: ComboOptionsViewProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [collapsedGroups, setCollapsedGroups] = React.useState<Record<string, boolean>>({})
  const toggleGroup = (group: string) => setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }))

  if (!open && !forceOpen) return null

  const deselectAllOptionValues = enableDeselectAllOption ? deselectAllOptions.map((d) => d.value) : []
  const pinnedOptions = enableDeselectAllOption ? options?.filter((o) => deselectAllOptionValues.includes(o.value)) : []
  const scrollableOptions = enableDeselectAllOption
    ? options?.filter((o) => !deselectAllOptionValues.includes(o.value))
    : options

  const renderOption = (option: OptionType, extraOptionClassName?: string) => (
    <OptionRow
      key={`${option.id}-${option.name}`}
      option={option}
      value={value}
      optionClassName={cn(optionClassName, extraOptionClassName)}
      multiple={multiple}
      selected={selected}
      enableDeselectAllOption={enableDeselectAllOption}
      deselectAllOptions={deselectAllOptions}
      checkboxSide={checkboxSide}
      allowDeselectOption={allowDeselectOption}
      // Only the grouped popover has a fixed width to clip against; the rest grow to fit.
      truncateLabel={isOptionsGrouped}
      onSelect={onSelect}
    />
  )

  // Hoist the current value to the top so it is visible without scrolling a long list.
  // Only for flat single-select lists: multi-select has no single current value, and
  // grouped lists would lose the option out of its group header. Matching mirrors
  // `isSelected` in OptionRow. When search filters the current value out, nothing is
  // hoisted and the list renders in its natural order.
  const selectedOption =
    !multiple && !isOptionsGrouped && value ? scrollableOptions?.find((o) => o.name === value.name) : undefined
  const unselectedOptions = selectedOption ? scrollableOptions?.filter((o) => o !== selectedOption) : scrollableOptions

  // Pinned (deselect-all) options and regular options render inside the SAME scroll
  // container so the scrollbar gutter affects every row uniformly and the checkboxes
  // stay aligned. The `.filter-option-all` class uses `order: -2` to float the pinned
  // option(s) above the hoisted current value. Used here only to decide between the
  // list and the empty state; the branches below control the actual render order.
  const orderedOptions = [...(pinnedOptions ?? []), ...(scrollableOptions ?? [])]

  return (
    <div
      className={cn(
        `flex max-h-96 flex-col overflow-hidden capitalize sm:text-body-xs sm:text-strong`,
        optionsContainerClassName,
        `w-full`,
      )}
      onWheel={(evt) => evt.stopPropagation()}
    >
      {showSearchInDropdown && (
        <ComboInputSearch ref={inputRef} handleSearchValueChange={onSearchValueChange} onClearSearch={onClearSearch} />
      )}
      <div className="flex flex-1 flex-col overflow-y-auto">
        {loading ? (
          <Dots />
        ) : orderedOptions.length ? (
          isOptionsGrouped ? (
            <>
              {/*
                Pinned (deselect-all) options render first, above the groups, ungrouped —
                they float to the top of the flex list via `.filter-option-all { order: -2 }`,
                same as the non-grouped path. Grouped filters today don't enable deselect-all;
                if one does, revisit whether the pinned row needs extra visual separation here.
              */}
              {pinnedOptions?.map((option) => renderOption(option))}
              {groupOptionsByMeta(scrollableOptions ?? []).map(([group, groupOptions], groupIndex) => (
                <div key={group}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group)}
                    className={cn(
                      `flex w-full items-center justify-between py-2 pl-4 pr-[6px]`,
                      groupIndex !== 0 ? `border-t border-default` : ``,
                    )}
                  >
                    <span className="text-ui-sm text-strong">{group}</span>
                    <ChevronDownIcon className={cn(`h-5 w-5`, collapsedGroups[group] ? `` : `rotate-180 transform`)} />
                  </button>
                  {!collapsedGroups[group] && groupOptions.map((option) => renderOption(option))}
                </div>
              ))}
            </>
          ) : (
            <>
              {pinnedOptions?.map((option) => renderOption(option))}
              {selectedOption &&
                renderOption(selectedOption, unselectedOptions?.length ? `border-b border-default` : undefined)}
              {unselectedOptions?.map((option) => renderOption(option))}
            </>
          )
        ) : (
          <span className="combobox-option !text-caption-md !text-subtle">No results found</span>
        )}
      </div>
      {!!onAddNewValue && (
        <div
          onClick={() => onAddNewValue(inputRef.current?.value ?? ``)}
          className="cursor-pointer border-t border-default p-2 text-ui-xs text-default hover:bg-(--menu-hover)"
        >
          {`+ ${addText}`}
        </div>
      )}
    </div>
  )
}
